import { Component, OnInit, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../core/services/auth.service';
import { AuthenticationService } from '../../core/services/authentication.service';
import { UserService, SelfProfile } from '../../core/services/user.service';
import {
  MemberChangeRequestService,
  MemberChangeRequestDto,
  ChangeRequestField
} from '../../core/services/member-change-request.service';
import { ToastService } from '../../core/services/toast.service';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, SpinnerComponent],
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.css']
})
export class SettingsComponent implements OnInit {
  private fb = inject(FormBuilder);
  private auth = inject(AuthService);
  private authentication = inject(AuthenticationService);
  private userService = inject(UserService);
  private changeRequestService = inject(MemberChangeRequestService);
  private toast = inject(ToastService);

  readonly isAdmin = computed(() =>
    this.auth.isSuperAdminSignal() || this.auth.isTenantAdminSignal());

  profile: SelfProfile | null = null;
  isLoadingProfile = false;
  profileError = '';

  contactForm: FormGroup = this.fb.group({
    email: ['', [Validators.email]],
    phone: ['', [Validators.required, Validators.minLength(7)]]
  });
  isSavingContact = false;
  contactMessage = '';
  contactError = '';

  passwordForm: FormGroup = this.fb.group({
    currentPassword: ['', [Validators.required]],
    newPassword:     ['', [Validators.required, Validators.minLength(4)]],
    confirmPassword: ['', [Validators.required]]
  }, { validators: this.passwordsMatchValidator });
  isSavingPassword = false;
  passwordMessage = '';
  passwordError = '';

  myRequests: MemberChangeRequestDto[] = [];
  isLoadingRequests = false;

  // Inline "request change" dialog for NIC (members can't edit directly anywhere).
  showNicRequest = false;
  nicValue = '';
  nicReason = '';
  isSubmittingNic = false;

  ngOnInit(): void {
    if (!this.isAdmin()) {
      this.loadProfile();
      this.loadMyRequests();
    }
  }

  private loadProfile(): void {
    this.isLoadingProfile = true;
    this.profileError = '';
    this.userService.getMyProfile().subscribe({
      next: (res) => {
        this.isLoadingProfile = false;
        if (res.success && res.data) {
          this.profile = res.data;
          this.contactForm.patchValue({
            email: res.data.email ?? '',
            phone: res.data.phone ?? ''
          });
        } else {
          this.profileError = res.message || 'Failed to load profile.';
        }
      },
      error: (err) => {
        this.isLoadingProfile = false;
        this.profileError = err?.error?.message || 'Failed to load profile.';
      }
    });
  }

  loadMyRequests(): void {
    this.isLoadingRequests = true;
    this.changeRequestService.getMyRequests().subscribe({
      next: (res) => {
        this.isLoadingRequests = false;
        if (res.success && Array.isArray(res.data)) {
          this.myRequests = res.data;
        }
      },
      error: () => { this.isLoadingRequests = false; }
    });
  }

  saveContact(): void {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      this.contactError = 'Please fix the highlighted fields.';
      return;
    }
    const v = this.contactForm.value;
    this.isSavingContact = true;
    this.contactMessage = '';
    this.contactError = '';
    this.userService.updateMyContact({
      email: v.email?.trim() || null,
      phone: v.phone.trim()
    }).subscribe({
      next: (res) => {
        this.isSavingContact = false;
        if (res.success) {
          if (res.pending) {
            this.contactMessage = res.message
              || 'Submitted for admin approval. You will be notified once reviewed.';
            this.toast.info('Change request submitted', this.contactMessage);
            this.loadMyRequests();
          } else {
            this.contactMessage = 'Contact details updated.';
            this.loadProfile();
          }
        } else {
          this.contactError = res.message || 'Update failed.';
        }
      },
      error: (err) => {
        this.isSavingContact = false;
        this.contactError = err?.error?.message || 'Update failed.';
      }
    });
  }

  savePassword(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      this.passwordError = this.passwordForm.errors?.['mismatch']
        ? 'New password and confirmation do not match.'
        : 'Please fix the highlighted fields.';
      return;
    }
    const v = this.passwordForm.value;
    this.isSavingPassword = true;
    this.passwordMessage = '';
    this.passwordError = '';
    this.authentication.changePassword(v.currentPassword, v.newPassword).subscribe({
      next: (res: any) => {
        this.isSavingPassword = false;
        if (res.success) {
          this.passwordMessage = 'Password updated.';
          this.passwordForm.reset({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } else {
          this.passwordError = res.message || 'Password change failed.';
        }
      },
      error: (err: any) => {
        this.isSavingPassword = false;
        this.passwordError = err?.error?.message || 'Password change failed.';
      }
    });
  }

  hasPendingForField(field: ChangeRequestField): boolean {
    return this.myRequests.some(r => r.status === 'Pending' && r.fieldName === field);
  }

  openNicRequest(): void {
    if (this.hasPendingForField('NIC')) {
      this.toast.warning('Request already pending',
        'You already have a pending NIC change request. Cancel it before submitting a new one.');
      return;
    }
    this.nicValue = '';
    this.nicReason = '';
    this.showNicRequest = true;
  }

  closeNicRequest(): void {
    this.showNicRequest = false;
    this.nicValue = '';
    this.nicReason = '';
    this.isSubmittingNic = false;
  }

  submitNicRequest(): void {
    const value = this.nicValue.trim();
    if (!value) {
      this.toast.warning('NIC required', 'Please enter the new NIC number.');
      return;
    }
    this.isSubmittingNic = true;
    this.changeRequestService.createMyRequest({
      fieldName: 'NIC',
      newValue: value,
      reason: this.nicReason.trim() || null
    }).subscribe({
      next: (res) => {
        this.isSubmittingNic = false;
        if (res.success) {
          this.toast.success('Request submitted', 'Your NIC change will be reviewed by an admin.');
          this.closeNicRequest();
          this.loadMyRequests();
        } else {
          this.toast.error('Request failed', res.message || 'Unable to submit the request.');
        }
      },
      error: (err) => {
        this.isSubmittingNic = false;
        this.toast.error('Request failed', err?.error?.message || 'Unable to submit the request.');
      }
    });
  }

  cancelRequest(r: MemberChangeRequestDto): void {
    this.changeRequestService.cancelMyRequest(r.requestId).subscribe({
      next: (res) => {
        if (res.success) {
          this.toast.success('Cancelled', 'Change request cancelled.');
          this.loadMyRequests();
        } else {
          this.toast.error('Cancel failed', res.message || 'Unable to cancel the request.');
        }
      },
      error: (err) => {
        this.toast.error('Cancel failed', err?.error?.message || 'Unable to cancel the request.');
      }
    });
  }

  statusBadgeClass(status: string): string {
    switch (status) {
      case 'Pending':  return 'bg-amber-100 text-amber-700';
      case 'Approved': return 'bg-green-100 text-green-700';
      case 'Rejected': return 'bg-red-100 text-red-700';
      default:         return 'bg-gray-100 text-gray-600';
    }
  }

  private passwordsMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const newPwd = group.get('newPassword')?.value;
    const confirm = group.get('confirmPassword')?.value;
    return newPwd && confirm && newPwd !== confirm ? { mismatch: true } : null;
  }
}
